import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader, PageBody } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Users, Send, MailOpen, MousePointerClick, ListChecks, AlertTriangle, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";

function Stat({ label, value, sub, testid, icon: Icon }) {
  return (
    <div
      className="border border-border bg-white p-5 flex flex-col"
      data-testid={testid}
    >
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
          {label}
        </span>
        <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="font-heading text-3xl md:text-4xl tracking-tight mt-4">
        {value}
      </div>
      {sub && <div className="text-xs text-muted-foreground mt-2">{sub}</div>}
    </div>
  );
}

export default function DashboardPage() {
  const [stats, setStats] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard/stats").then(({ data }) => setStats(data));
  }, []);

  return (
    <>
      <PageHeader
        title={`Buongiorno${user?.name ? ", " + user.name.split(" ")[0] : ""}`}
        subtitle="Riepilogo delle tue newsletter e delle performance recenti."
        testid="dashboard-header"
        actions={
          <Button
            onClick={() => navigate("/campagne/nuova")}
            data-testid="dashboard-new-campaign"
          >
            Nuova campagna
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        }
      />
      <PageBody>
        {!user?.microsoft_connected && (
          <div
            className="border border-border bg-white p-4 flex items-start gap-3 mb-6"
            data-testid="ms-warning"
          >
            <AlertTriangle className="w-5 h-5 text-destructive shrink-0 mt-0.5" strokeWidth={1.5} />
            <div className="flex-1">
              <div className="font-medium text-sm">
                Microsoft 365 non è ancora collegato
              </div>
              <div className="text-sm text-muted-foreground">
                Per inviare newsletter devi collegare il tuo account Microsoft 365 dalle{" "}
                <button
                  className="text-primary underline hover:no-underline"
                  onClick={() => navigate("/impostazioni")}
                  data-testid="link-to-settings"
                >
                  Impostazioni
                </button>
                .
              </div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Stat
            label="Contatti attivi"
            value={stats?.active_contacts ?? "—"}
            sub={`${stats?.unsubscribed ?? 0} disiscritti`}
            testid="stat-contacts"
            icon={Users}
          />
          <Stat
            label="Liste"
            value={stats?.total_lists ?? "—"}
            sub="Segmenti di contatti"
            testid="stat-lists"
            icon={ListChecks}
          />
          <Stat
            label="Campagne inviate"
            value={stats?.sent_campaigns ?? "—"}
            sub={`${stats?.total_sent ?? 0} email totali`}
            testid="stat-campaigns"
            icon={Send}
          />
          <Stat
            label="Tasso aperture (uniche)"
            value={`${stats?.open_rate ?? 0}%`}
            sub={`${stats?.unique_opens ?? 0} aperture uniche · ${stats?.total_opens ?? 0} totali · ${stats?.unique_clicks ?? 0} click unici`}
            testid="stat-open-rate"
            icon={MailOpen}
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 border border-border bg-white">
            <div className="border-b border-border px-6 py-4 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-lg">Campagne recenti</h2>
                <p className="text-xs text-muted-foreground mt-1">
                  Ultime attività della tua area invio
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/campagne")}
                data-testid="view-all-campaigns"
              >
                Vedi tutte
              </Button>
            </div>
            <div className="divide-y divide-border">
              {(stats?.recent_campaigns || []).length === 0 && (
                <div className="p-8 text-center text-muted-foreground">
                  Nessuna campagna ancora.{" "}
                  <button
                    onClick={() => navigate("/campagne/nuova")}
                    className="text-primary underline"
                    data-testid="empty-create-campaign"
                  >
                    Creane una
                  </button>
                </div>
              )}
              {(stats?.recent_campaigns || []).map((c) => (
                <div
                  key={c.id}
                  className="px-6 py-4 flex items-center justify-between hover:bg-muted/40 cursor-pointer"
                  onClick={() => navigate(`/campagne/${c.id}`)}
                  data-testid={`recent-campaign-${c.id}`}
                >
                  <div>
                    <div className="font-medium text-sm">{c.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {c.subject}
                    </div>
                  </div>
                  <div className="flex items-center gap-6 text-xs text-muted-foreground">
                    <span className="uppercase tracking-widest">{c.status}</span>
                    <MousePointerClick className="w-4 h-4" strokeWidth={1.5} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-border bg-white">
            <div className="border-b border-border px-6 py-4">
              <h2 className="font-heading text-lg">Azioni rapide</h2>
              <p className="text-xs text-muted-foreground mt-1">
                Per iniziare velocemente
              </p>
            </div>
            <div className="p-6 space-y-3">
              <Button
                className="w-full justify-between"
                variant="outline"
                onClick={() => navigate("/contatti/importa")}
                data-testid="quick-import"
              >
                Importa contatti da CSV
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                className="w-full justify-between"
                variant="outline"
                onClick={() => navigate("/liste")}
                data-testid="quick-lists"
              >
                Crea una lista
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                className="w-full justify-between"
                variant="outline"
                onClick={() => navigate("/template")}
                data-testid="quick-templates"
              >
                Sfoglia template
                <ArrowRight className="w-4 h-4" />
              </Button>
              <Button
                className="w-full justify-between"
                onClick={() => navigate("/campagne/nuova")}
                data-testid="quick-new-campaign"
              >
                Crea una campagna
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </PageBody>
    </>
  );
}
